import { useIntl } from "react-intl";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
// import MiniList from "@lib/components/MiniComponents/Dashboard/MiniList";
import { Col } from "antd";
import { useMemo } from "react";
import { ApiParams, CurrencyEnum } from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomerLeadScannedTransaction } from "@lib/api/walletSendByte";
import Revenue from "@lib/components/MiniComponents/Dashboard/Revenue";
import CustomPieChart from "@lib/components/MiniComponents/Dashboard/PieChart";
import {
  CoinPriceTextFormat,
  DateRangeFilter,
} from "@lib/components/MiniComponents/Others";
import { randomColor } from "@lib/helpers/ColorHelper";
import MonthlySendbyteChart from "@lib/components/MiniComponents/Dashboard/MonthlySendbyteChart";

type Props = {
  dateBetween: [string, string];
  setDateBetween: (date: [string, string]) => void;
};

const ManagementSendbyte: React.FC<Props> = ({ dateBetween }) => {
  const { messages } = useIntl();

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
      join: ["customer"],
    } as ApiParams;
  }, [dateBetween]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryFn: () => apiGetCustomerLeadScannedTransaction(params),
    queryKey: ["sendbyte-payments", JSON.stringify(params)],
    enabled: !!params,
  });
  const apiData = data?.data?.data;
  const customers = apiData
    ?.map((item) => item?.customer)
    .filter(
      (customer, index, self) =>
        index === self.findIndex((t) => t?.id === customer?.id),
    );

  const totalAmount = useMemo(() => {
    return apiData?.reduce(
      (acc, curr) => acc + Number(curr?.convertedAmount || 0),
      0,
    );
  }, [apiData]);
  const totalPaid = useMemo(() => {
    // confirmed == "true"
    return apiData?.reduce((acc, curr) => {
      if (curr?.confirmed === "true") {
        return acc + Number(curr?.convertedAmount || 0);
      }
      return acc;
    }, 0);
  }, [apiData]);

  const customersGroup = useMemo(() => {
    const data: Record<string, number> = {};
    apiData?.forEach((item) => {
      const customerId = item?.customerId;
      if (!data[customerId]) {
        data[customerId] = 0;
      }
      data[customerId] += Number(item?.convertedAmount || 0);
    });
    return Object.entries(data).map(([customerId, amount], index) => ({
      id: customerId,
      name:
        customers?.find((customer) => customer?.id === customerId)?.name || "",
      value: amount,
      valueText: (
        <CoinPriceTextFormat
          currency={CurrencyEnum.USD}
          amount={amount}
          showCurrency
        />
      ),
      color: randomColor(index),
    }));
  }, [apiData]);

  return (
    <AppRowContainer>
      <Col xs={24} md={12} lg={8}>
        <Revenue
          title={messages["common.revenue"] as string}
          data={{
            currency: CurrencyEnum.USD,
            paid: totalPaid,
            total: totalAmount,
          }}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          refetch={refetch}
          loading={isLoading || isFetching}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={8}>
        <CustomPieChart
          title={messages["common.invoiceStatus"] as string}
          graph="graph2"
          data={customersGroup}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          refetch={refetch}
          loading={isLoading || isFetching}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={8}>
        <MonthlySendbyteChart />
      </Col>
    </AppRowContainer>
  );
};

export default ManagementSendbyte;
